import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { db } from '@/config/firebase'
import { doc, onSnapshot } from 'firebase/firestore'
import { useAuthStore } from './auth'

interface UserData {
  name: string
  email: string
  org_id: string | null
  role: string
}

export const useUserStore = defineStore('user', () => {
  const userData = ref<UserData | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)
  const unsubscribe = ref<(() => void) | null>(null)

  const hasOrg = computed(() => {
    return !!userData.value?.org_id
  })

  const startListening = () => {
    const authStore = useAuthStore()
    if (!authStore.user?.uid) return

    // Clean up any existing listener
    if (unsubscribe.value) {
      unsubscribe.value()
    }

    loading.value = true
    error.value = null

    // Start listening to user document
    unsubscribe.value = onSnapshot(
      doc(db, 'users', authStore.user.uid),
      (doc) => {
        if (doc.exists()) {
          userData.value = doc.data() as UserData
        } else {
          userData.value = null
        }
        loading.value = false
      },
      (err) => {
        console.error('Error listening to user document:', err)
        error.value = err.message
        loading.value = false
      }
    )
  }

  const stopListening = () => {
    if (unsubscribe.value) {
      unsubscribe.value()
      unsubscribe.value = null
    }
    userData.value = null
    loading.value = false
    error.value = null
  }

  return {
    userData,
    loading,
    error,
    hasOrg,
    startListening,
    stopListening
  }
})
